import { useEffect, useState, useCallback } from 'react';

export type ThemePref = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'relay-theme';

/** Resolve the effective theme ('light'|'dark') for a preference. */
function resolve(pref: ThemePref): 'light' | 'dark' {
  if (pref === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return pref;
}

/** Read the stored preference (defaults to 'light' — the app's original look). */
export function getStoredThemePref(): ThemePref {
  const v = localStorage.getItem(STORAGE_KEY);
  return v === 'light' || v === 'dark' || v === 'system' ? v : 'light';
}

/** Apply the resolved theme to the document root. */
export function applyTheme(pref: ThemePref): void {
  document.documentElement.setAttribute('data-theme', resolve(pref));
}

/**
 * Per-device theme preference, persisted in localStorage. Sets `data-theme`
 * on <html> so CSS variables flip. Listens for OS changes when set to 'system'.
 */
export function useTheme() {
  const [pref, setPref] = useState<ThemePref>(getStoredThemePref);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, pref);
    applyTheme(pref);

    if (pref !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme('system');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [pref]);

  const setTheme = useCallback((next: ThemePref) => setPref(next), []);

  return { pref, setTheme };
}
